/**
 * Stocktake Offline Service — reusable offline platform capability
 *
 * Manages the full lifecycle of offline stocktake sessions:
 *  1. Download session package (API → IndexedDB)
 *  2. Local CRUD (read/write IndexedDB)
 *  3. Sync back to central (IndexedDB → API)
 *  4. Sync state tracking (Pending / Synced / Conflict)
 *
 * Designed as a generic pattern: the same download → local-work → sync-back
 * approach can be adopted by other AbbeyZone features in the future.
 */

import offlineDb from './offlineDb';
import api from './api';

const { STORES } = offlineDb;

// Sync states — structural constants used across the platform
export const SYNC_STATES = {
  PENDING: 'pending',     // Downloaded, has local changes not yet synced
  SYNCED: 'synced',       // All changes successfully synced to central
  CONFLICT: 'conflict',   // Server modified since download — needs resolution
  DOWNLOADING: 'downloading',
  ERROR: 'error',
};


/**
 * Download a stocktake session package for offline use.
 * Fetches everything needed to complete the wizard offline.
 *
 * iter383 — When the requested session is a multi-location parent,
 * the server bundles every child + every internal stock transfer
 * under `pkg.multi_location`. We unpack that bundle into one
 * IndexedDB row per session (parent + each child) so navigation
 * across the audit family offline uses the same per-session loader
 * with no fallback logic. Each child also receives a copy of the
 * shared masters (products, suppliers, recipes, costing policy …)
 * AND the same transfers slice — so an auditor working a child
 * offline can value transfers_in/out without needing the parent.
 */
export async function downloadSessionPackage(sessionId) {
  try {
    const response = await api.get(`/offline/sessions/${sessionId}/package`);
    const pkg = response.data;

    const childPackages = pkg?.multi_location?.children || [];
    const transfers = pkg?.multi_location?.transfers || [];
    const sharedMasters = {
      products: pkg.products,
      suppliers: pkg.suppliers,
      site_inventory: pkg.site_inventory,
      locations: pkg.locations,
      count_sheet_templates: pkg.count_sheet_templates,
      costing_policy: pkg.costing_policy,
      org_stocktaking_settings: pkg.org_stocktaking_settings,
      recipes: pkg.recipes,
    };

    // ── Persist the parent / root session package ──
    const parentPkg = { ...pkg, transfers };
    // The `multi_location` envelope is no longer needed once we've
    // expanded it. Stripping keeps each IndexedDB row lean.
    delete parentPkg.multi_location;
    await offlineDb.put(STORES.STOCKTAKE_PACKAGES, {
      id: sessionId,
      package: parentPkg,
      sync_state: SYNC_STATES.SYNCED,
      downloaded_at: pkg.downloaded_at,
      server_session_updated_at: pkg.server_session_updated_at || null,
      session_reference: pkg.session?.session_reference,
      session_date: pkg.session?.session_date,
      site_name: pkg.session?.site_name,
      session_type: pkg.session?.session_type,
      local_changes: null,
      last_synced_at: new Date().toISOString(),
      conflict_details: null,
    });

    // ── Persist each child as its own IndexedDB row ──
    const savedChildIds = [];
    for (const child of childPackages) {
      // Re-hydrate shared masters that the server stripped to save
      // payload size — every child must be self-contained so its
      // calc / wizard renders identically to the parent's.
      const hydrated = { ...child, ...sharedMasters, transfers };
      delete hydrated.multi_location;
      const cid = child?.session?.id;
      if (!cid) continue;
      await offlineDb.put(STORES.STOCKTAKE_PACKAGES, {
        id: cid,
        package: hydrated,
        sync_state: SYNC_STATES.SYNCED,
        downloaded_at: pkg.downloaded_at,
        server_session_updated_at: null,
        session_reference: child.session?.session_reference,
        session_date: child.session?.session_date,
        site_name: child.session?.site_name || pkg.session?.site_name,
        session_type: child.session?.session_type,
        local_changes: null,
        last_synced_at: new Date().toISOString(),
        conflict_details: null,
      });
      savedChildIds.push(cid);
    }

    return {
      success: true,
      package: parentPkg,
      bundled_children: savedChildIds,
      transfers_count: transfers.length,
    };
  } catch (error) {
    console.error('Failed to download session package:', error);
    return { success: false, error: error.message };
  }
}


/**
 * Get all downloaded offline session packages.
 */
export async function getOfflineSessionList() {
  try {
    const all = await offlineDb.getAll(STORES.STOCKTAKE_PACKAGES);
    return all.map(item => ({
      id: item.id,
      session_reference: item.session_reference,
      session_date: item.session_date,
      site_name: item.site_name,
      session_type: item.session_type,
      sync_state: item.sync_state,
      downloaded_at: item.downloaded_at,
      last_synced_at: item.last_synced_at,
      has_local_changes: !!item.local_changes,
      conflict_details: item.conflict_details,
    }));
  } catch (error) {
    console.error('Failed to get offline sessions:', error);
    return [];
  }
}


/**
 * Load a full offline session package from IndexedDB.
 */
export async function loadOfflinePackage(sessionId) {
  try {
    const record = await offlineDb.get(STORES.STOCKTAKE_PACKAGES, sessionId);
    if (!record) return null;
    return record;
  } catch (error) {
    console.error('Failed to load offline package:', error);
    return null;
  }
}


/**
 * Save local changes to an offline session.
 * Updates the package record with modified entries and marks as pending.
 */
export async function saveLocalChanges(sessionId, changes) {
  try {
    const record = await offlineDb.get(STORES.STOCKTAKE_PACKAGES, sessionId);
    if (!record) throw new Error('Session not found in offline storage');

    record.local_changes = {
      ...record.local_changes,
      ...changes,
      updated_at: new Date().toISOString(),
    };
    record.sync_state = SYNC_STATES.PENDING;

    await offlineDb.put(STORES.STOCKTAKE_PACKAGES, record);
    return { success: true };
  } catch (error) {
    console.error('Failed to save local changes:', error);
    return { success: false, error: error.message };
  }
}


/**
 * Save local calculation result to offline session.
 */
export async function saveLocalCalculation(sessionId, calculationResult) {
  try {
    const record = await offlineDb.get(STORES.STOCKTAKE_PACKAGES, sessionId);
    if (!record) throw new Error('Session not found in offline storage');

    record.local_changes = {
      ...record.local_changes,
      calculation_snapshot: calculationResult,
      updated_at: new Date().toISOString(),
    };
    record.sync_state = SYNC_STATES.PENDING;

    await offlineDb.put(STORES.STOCKTAKE_PACKAGES, record);
    return { success: true };
  } catch (error) {
    console.error('Failed to save local calculation:', error);
    return { success: false, error: error.message };
  }
}


/**
 * Queue an "AddFromMaster" assignment while offline — pushes a record into
 * `local_changes.site_inventory_additions` so the next sync inserts the
 * SiteBeverageInventory row + reconciles the count sheet.
 *
 * Idempotent on (sessionId, beverage_product_id). Returns the updated
 * additions array on success.
 */
export async function queueOfflineSiteInventoryAddition(sessionId, product) {
  try {
    if (!product?.id) return { success: false, error: 'Missing product id' };
    const record = await offlineDb.get(STORES.STOCKTAKE_PACKAGES, sessionId);
    if (!record) throw new Error('Session not found in offline storage');

    const existing = record.local_changes || {};
    const additions = Array.isArray(existing.site_inventory_additions)
      ? [...existing.site_inventory_additions]
      : [];
    if (!additions.some(a => Number(a.beverage_product_id) === Number(product.id))) {
      additions.push({ beverage_product_id: product.id });
    }

    record.local_changes = {
      ...existing,
      site_inventory_additions: additions,
      updated_at: new Date().toISOString(),
    };
    record.sync_state = SYNC_STATES.PENDING;
    await offlineDb.put(STORES.STOCKTAKE_PACKAGES, record);
    return { success: true, additions };
  } catch (error) {
    console.error('Failed to queue offline site_inventory_addition:', error);
    return { success: false, error: error.message };
  }
}


/**
 * Queue a Site Inventory Setup measure/yield override change while offline.
 *
 * Records `{ beverage_product_id, action: 'upsert' | 'delete', measure_ml,
 * measure_label, nominal_servings_per_unit }` into
 * `local_changes.site_product_measure_changes`. Last-write-wins per product.
 */
export async function queueOfflineMeasureChange(sessionId, change) {
  try {
    if (!change?.beverage_product_id) {
      return { success: false, error: 'Missing beverage_product_id' };
    }
    const record = await offlineDb.get(STORES.STOCKTAKE_PACKAGES, sessionId);
    if (!record) throw new Error('Session not found in offline storage');

    const existing = record.local_changes || {};
    const queue = Array.isArray(existing.site_product_measure_changes)
      ? existing.site_product_measure_changes.filter(
          c => Number(c.beverage_product_id) !== Number(change.beverage_product_id)
        )
      : [];
    queue.push(change);

    record.local_changes = {
      ...existing,
      site_product_measure_changes: queue,
      updated_at: new Date().toISOString(),
    };
    record.sync_state = SYNC_STATES.PENDING;
    await offlineDb.put(STORES.STOCKTAKE_PACKAGES, record);
    return { success: true, queue };
  } catch (error) {
    console.error('Failed to queue offline measure change:', error);
    return { success: false, error: error.message };
  }
}


/**
 * Sync an offline session back to the central database.
 * Detects conflicts: if server was modified after download, sync is rejected.
 */
export async function syncSessionToServer(sessionId) {
  try {
    const record = await offlineDb.get(STORES.STOCKTAKE_PACKAGES, sessionId);
    if (!record) throw new Error('Session not found in offline storage');
    if (!record.local_changes) {
      // Nothing to sync
      record.sync_state = SYNC_STATES.SYNCED;
      await offlineDb.put(STORES.STOCKTAKE_PACKAGES, record);
      return { success: true, sync_status: 'synced' };
    }

    const payload = {
      package_schema_version: record.package?.schema_version || 1,
      downloaded_at: record.downloaded_at,
      server_session_updated_at: record.server_session_updated_at || null,
      count_entries: record.local_changes.count_entries || [],
      sales_entries: record.local_changes.sales_entries || [],
      adjustment_entries: record.local_changes.adjustment_entries || [],
      offline_deliveries: record.local_changes.offline_deliveries || [],
      offline_credits: record.local_changes.offline_credits || [],
      imported_observations: record.local_changes.imported_observations || [],
      // iter340 v2 — products assigned from inventory master while offline.
      // The server inserts SiteBeverageInventory rows + reconciles the count
      // sheet, so synthetic count entries are remapped to the real DB lines.
      site_inventory_additions: record.local_changes.site_inventory_additions || [],
      // iter340 v6 — site-specific measure / yield override changes
      // (upsert + delete) queued from the Site Inventory Setup modal.
      site_product_measure_changes: record.local_changes.site_product_measure_changes || [],
      calculation_snapshot: record.local_changes.calculation_snapshot || null,
      session_status: record.local_changes.session_status || null,
      session_notes: record.local_changes.session_notes || null,
      // Phase 4 — Actual Receipts entered offline flows back to the session
      // so the online revised headline / blocker honours the offline input.
      accounts_sales_incl_vat: record.local_changes.accounts_sales_incl_vat ?? null,
      accounts_sales_notes: record.local_changes.accounts_sales_notes ?? null,
    };

    const response = await api.post(`/offline/sessions/${sessionId}/sync`, payload);
    const result = response.data;

    if (result.sync_status === 'synced') {
      record.sync_state = SYNC_STATES.SYNCED;
      record.local_changes = null;
      record.last_synced_at = result.synced_at || new Date().toISOString();
      record.conflict_details = null;
    } else if (result.sync_status === 'conflict') {
      record.sync_state = SYNC_STATES.CONFLICT;
      record.conflict_details = result.conflict_details;
    }

    await offlineDb.put(STORES.STOCKTAKE_PACKAGES, record);
    return { success: true, ...result };
  } catch (error) {
    console.error('Failed to sync session:', error);

    // Mark as error in local store
    try {
      const record = await offlineDb.get(STORES.STOCKTAKE_PACKAGES, sessionId);
      if (record) {
        record.sync_state = SYNC_STATES.ERROR;
        record.conflict_details = { reason: error.message };
        await offlineDb.put(STORES.STOCKTAKE_PACKAGES, record);
      }
    } catch (e) { /* ignore nested error */ }

    return { success: false, error: error.message };
  }
}


/**
 * Sync ALL pending offline sessions.
 */
export async function syncAllPendingSessions() {
  const sessions = await getOfflineSessionList();
  const pending = sessions.filter(s => s.sync_state === SYNC_STATES.PENDING);
  const results = { synced: 0, conflicts: 0, errors: 0 };

  for (const session of pending) {
    const result = await syncSessionToServer(session.id);
    if (result.sync_status === 'synced') results.synced++;
    else if (result.sync_status === 'conflict') results.conflicts++;
    else results.errors++;
  }

  return results;
}


/**
 * Remove an offline session package from local storage.
 */
export async function removeOfflinePackage(sessionId) {
  try {
    await offlineDb.remove(STORES.STOCKTAKE_PACKAGES, sessionId);
    return { success: true };
  } catch (error) {
    console.error('Failed to remove offline package:', error);
    return { success: false, error: error.message };
  }
}


/**
 * Check if a session is available offline.
 */
export async function isSessionAvailableOffline(sessionId) {
  try {
    const record = await offlineDb.get(STORES.STOCKTAKE_PACKAGES, sessionId);
    return !!record;
  } catch {
    return false;
  }
}


/**
 * Get the sync state of an offline session.
 */
export async function getSessionSyncState(sessionId) {
  try {
    const record = await offlineDb.get(STORES.STOCKTAKE_PACKAGES, sessionId);
    if (!record) return null;
    return {
      sync_state: record.sync_state,
      has_local_changes: !!record.local_changes,
      downloaded_at: record.downloaded_at,
      last_synced_at: record.last_synced_at,
      conflict_details: record.conflict_details,
    };
  } catch {
    return null;
  }
}

export default {
  SYNC_STATES,
  downloadSessionPackage,
  getOfflineSessionList,
  loadOfflinePackage,
  saveLocalChanges,
  saveLocalCalculation,
  queueOfflineSiteInventoryAddition,
  queueOfflineMeasureChange,
  syncSessionToServer,
  syncAllPendingSessions,
  removeOfflinePackage,
  isSessionAvailableOffline,
  getSessionSyncState,
};
